import type { TickerWorkspace, TickerWorkspaceStrategy } from "@/types/api";
import type { StrategyLayer } from "@/types/strategy";
import { request } from "./transport";

export interface TickerWorkspaceInput {
  ticker: string;
  state?: Record<string, unknown>;
  position?: number;
}

export interface TickerWorkspaceStrategyInput {
  layer: StrategyLayer;
  enabled?: boolean;
  position?: number;
  simulator_tab_id?: string | null;
  saved_strategy_id?: string | null;
  source_layer_id?: string | null;
}

export interface TickerWorkspaceStrategyReplacement {
  id?: string;
  layer: StrategyLayer;
  enabled: boolean;
}

export interface TickerWorkspaceStrategiesPayload {
  ticker_workspace_strategies: TickerWorkspaceStrategy[];
  strategies_revision: number;
}

export const tickerWorkspaces = {
  listTickerWorkspaces: () =>
    request<{ ticker_workspaces: TickerWorkspace[] }>("/ticker_workspaces"),

  createTickerWorkspace: (input: TickerWorkspaceInput) =>
    request<{ ticker_workspace: TickerWorkspace }>("/ticker_workspaces", {
      method: "POST",
      body: JSON.stringify({ ticker_workspace: input }),
    }),

  updateTickerWorkspace: (
    id: string,
    patch: Partial<Pick<TickerWorkspaceInput, "state" | "position">>,
  ) =>
    request<{ ticker_workspace: TickerWorkspace }>(`/ticker_workspaces/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ ticker_workspace: patch }),
    }),

  archiveTickerWorkspace: (id: string) =>
    request<{ ok: true }>(`/ticker_workspaces/${id}`, { method: "DELETE" }),

  createTickerWorkspaceStrategy: (
    workspaceId: string,
    input: TickerWorkspaceStrategyInput,
  ) =>
    request<{
      ticker_workspace_strategy: TickerWorkspaceStrategy;
      ticker_workspace_strategies: TickerWorkspaceStrategy[];
      strategies_revision: number;
    }>(`/ticker_workspaces/${workspaceId}/strategies`, {
      method: "POST",
      body: JSON.stringify({ ticker_workspace_strategy: input }),
    }),

  updateTickerWorkspaceStrategy: (
    workspaceId: string,
    strategyId: string,
    patch: Partial<TickerWorkspaceStrategyInput>,
  ) =>
    request<{
      ticker_workspace_strategy: TickerWorkspaceStrategy;
      ticker_workspace_strategies: TickerWorkspaceStrategy[];
      strategies_revision: number;
    }>(`/ticker_workspaces/${workspaceId}/strategies/${strategyId}`, {
      method: "PATCH",
      body: JSON.stringify({ ticker_workspace_strategy: patch }),
    }),

  deleteTickerWorkspaceStrategy: (
    workspaceId: string,
    strategyId: string,
    withHistory = false,
  ) =>
    request<{
      ok: true;
      ticker_workspace_strategies: TickerWorkspaceStrategy[];
      strategies_revision: number;
    }>(
      `/ticker_workspaces/${workspaceId}/strategies/${strategyId}${withHistory ? "?with_history=true" : ""}`,
      {
        method: "DELETE",
      },
    ),

  replaceTickerWorkspaceStrategies: (
    workspaceId: string,
    baseRevision: number,
    strategies: TickerWorkspaceStrategyReplacement[],
  ) =>
    request<TickerWorkspaceStrategiesPayload>(
      `/ticker_workspaces/${workspaceId}/strategies/replace`,
      {
        method: "PATCH",
        body: JSON.stringify({
          base_revision: baseRevision,
          ticker_workspace_strategies: strategies,
        }),
      },
    ),
};
